// Pricing data: edge rows, six-tier subscriptions, expansion modules.
//
// Source of truth (in priority order):
//   1. STRIKZA_OddsJam_Gaps_v1.0.docx — the 15 named feature
//      specifications and their tier gates. Tier feature lists below
//      reference these by spec name (e.g. "Real-Time Odds
//      Comparison", "No-Vig Fair Odds", "Slip Autopsy").
//   2. STRIKZA_PRICING_FEATURES.md §2 / §14 — the six-tier structure
//      (Starter / Sika Pro / Pro / Elite / Full Stack / Black).
//   3. STRIKZA_DualSwarm_Architecture v1.0.0 — what each tier
//      receives from the dual-swarm engine.
//
// Doctrinal posture: tier features are described by named spec, not
// vague marketing copy. A user can map every line below to a row in
// the FeatureMatrix.

export interface EdgeRow {
  topic: string;
  house: string;
  strikza: string;
}

export const EDGE_ROWS: readonly EdgeRow[] = [
  {
    topic: "Pricing the line · the Benter principle",
    house:
      "Books shade odds 3-7% past true probability. They call it the vig. You call it Tuesday.",
    strikza:
      "STRIKZA's Fair Price Engine computes its own probability per fixture before consulting the market. Agent B only flags a slip when the book is wrong by a real margin.",
  },
  {
    topic: "Hot streak design · the Voulgaris discipline",
    house: "Variable rewards keep you placing. Near-misses are engineered. Free bets are anchors.",
    strikza:
      "Cold engine. The Risk Governor blocks emotional bets. The Debate Moderator makes every agent justify its confidence before consensus is reached.",
  },
  {
    topic: "Information asymmetry · the Walters edge",
    house: "Books move on signals you can't see. By the time the line shifts, you're stale.",
    strikza:
      "Agent B + the Odds Freshness Gate watch every priced book in real time. You see the line classified — steam move, public drift, or trap — before you stake.",
  },
];

export type Currency = "GHS" | "NGN";

export interface PriceTier {
  id: string;
  name: string;
  tag: string;
  tagline: string;
  features: readonly string[];
}

// Six tiers per STRIKZA_PRICING_FEATURES.md §2/§14. Order is ascending price.
// `pro` is featured (tag "Most Popular") per §1 — the "heart" of the product.
export const TIERS: readonly PriceTier[] = [
  {
    id: "starter",
    name: "STARTER",
    tag: "Tier 01",
    tagline:
      "First-time entry. Test the engine without commitment. Football intelligence at the price of a data bundle.",
    features: [
      "GAP-02 · No-Vig Fair Odds Display",
      "GAP-04 · Expected Value display per signal",
      "GAP-09 · Verified Return Certificate (90-day audit)",
      "GAP-14 · Free daily signal · 1 chain (max 10 wks)",
      "GAP-15 · Structured onboarding · Safe Mode mandatory",
    ],
  },
  {
    id: "sika_pro",
    name: "SIKA PRO",
    tag: "Tier 1.5 · New",
    tagline:
      "Zero-config goal planning. Tell SIKA what you want — MacBook, school fees, a car. SIKA manages everything.",
    features: [
      "Full SIKA conversation · 2 active goals",
      "GAP-01 · Real-Time Odds Comparison Screen",
      "GAP-03 · Kelly Criterion auto-sizing",
      "GAP-05 · Poisson Model Transparency Layer",
      "GAP-08 · Personal CLV Dashboard",
      "GAP-10 · Slip Autopsy",
    ],
  },
  {
    id: "pro",
    name: "PRO",
    tag: "Tier 02",
    tagline:
      "Multi-chain operators. Historical signal database, line-movement push alerts, full Daily Intelligence Ledger read access.",
    features: [
      "All Sika Pro features",
      "3 concurrent ROLLOVER chains (up to 20 wks)",
      "GAP-06 · Historical Odds + Signal Database",
      "GAP-07 · Line Movement Alert System",
      "Daily Intelligence Ledger · full read access",
      "Multi-chain SIKA goal plans",
    ],
  },
  {
    id: "elite",
    name: "ELITE",
    tag: "Tier 03",
    tagline:
      "The arbitrage / promo / middling layer. For users with capital across multiple bookmakers and a daily-driver intelligence need.",
    features: [
      "All Pro features",
      "Unlimited chains, no caps",
      "GAP-11 · Promo & Bonus Harvesting Engine",
      "GAP-12 · West African Arbitrage Scanner (Phase 2)",
      "GAP-13 · Middling Detection Engine (Phase 2)",
      "Backtesting Lab · 90 days",
    ],
  },
  {
    id: "fullstack",
    name: "FULL STACK",
    tag: "Tier 3.5",
    tagline:
      "Every doctrinal pipeline + every OddsJam-class spec + early access to the B2B research API.",
    features: [
      "All Elite features",
      "All six pipelines · Daily · POWERBALL 10 · MEGA 20 · LADDER · Singles · Owl",
      "B2B research API · early-access tier",
      "Extended backtesting · 180 days",
      "Resilience Audit · unlimited",
      "Sika Quarterly Strategy Review",
    ],
  },
  {
    id: "black",
    name: "BLACK",
    tag: "Tier 04 · 20 slots · Request Access",
    tagline:
      "SIKA Wealth Advisory. Quarterly founder + SIKA portfolio review. Dedicated agent infrastructure. Application required.",
    features: [
      "All Full Stack features",
      "SIKA Quarterly Wealth Review · 45 min",
      "Dedicated agent processing pool",
      "1:1 founder access",
      "Earliest access to new gap specs",
      "Hard-capped at 20 active members",
    ],
  },
];

// Subscription prices by currency. NGN ≈ 85× GHS, rounded to nearest hundred.
export const PRICES: { readonly [tier: string]: { readonly [c in Currency]: number } } = {
  starter: { GHS: 80, NGN: 6800 },
  sika_pro: { GHS: 200, NGN: 17000 },
  pro: { GHS: 550, NGN: 47000 },
  elite: { GHS: 1200, NGN: 102000 },
  fullstack: { GHS: 1800, NGN: 153000 },
  black: { GHS: 3500, NGN: 297500 },
};

// Bundle interface kept for the secondary toggle, but the bundle catalogue
// is now the four expansion modules from §14 (replacing the old "TRADER /
// SPECULATOR / FULL STACK" landing-narrative bundles, which conflicted with
// the actual six-tier pricing). Each module is an add-on to a base plan.
export interface Bundle {
  id: string;
  name: string;
  tag: string;
  tagline: string;
  modules: readonly string[];
  prices: { readonly [c in Currency]: number };
  save: { readonly GHS: number } | null;
  featured?: boolean;
}

export const BUNDLES: readonly Bundle[] = [
  {
    id: "pulse",
    name: "PULSE",
    tag: "Polymarket",
    tagline: "Prediction-market intelligence. Event-driven plays cycling 24/7 outside football.",
    modules: ["Polymarket signals", "Event resolution alerts", "Cross-book arbitrage flags"],
    prices: { GHS: 350, NGN: 30000 },
    save: null,
  },
  {
    id: "pip",
    name: "PIP",
    tag: "Forex",
    tagline:
      "Currency-pair signals with risk-managed entries. Built for the trader who already trades.",
    modules: [
      "Forex signals (majors + minors)",
      "Risk-managed entries + stops",
      "Session-aware timing",
    ],
    prices: { GHS: 600, NGN: 51000 },
    save: null,
  },
  {
    id: "block",
    name: "BLOCK",
    tag: "Crypto · Phase 2",
    tagline:
      "Crypto-market intelligence using the same probability-engine substrate as football. Read-only signals — STRIKZA never executes trades or connects to user accounts.",
    modules: [
      "Crypto signals (top 50 caps)",
      "STRIKZA Score weekly research",
      "Read-only · user places every trade",
    ],
    prices: { GHS: 650, NGN: 55000 },
    save: null,
    featured: true,
  },
  {
    id: "equity",
    name: "EQUITY",
    tag: "GSE / NSE",
    tagline:
      "Ghana Stock Exchange and Nigerian Exchange intelligence. The category nobody else covers.",
    modules: ["GSE + NSE coverage", "Quarterly thesis briefs", "Dividend + earnings flags"],
    prices: { GHS: 200, NGN: 17000 },
    save: null,
  },
];
